/**
 * Token Management Page
 * AIDLC Administrative Backend
 *
 * Full dashboard for tracking AI token spend, cost breakdowns,
 * budget configuration, and session-level detail logging.
 *
 * 7 sections:
 *   1. Executive Summary (metric cards)
 *   1b. Agent spend cap: today and this month, and the "Enforce spend caps" switch (CAP-TOGGLE-1)
 *   2. Cost Breakdown by Agent (table)
 *   3. Cost by Project (doughnut chart + cards)
 *   3b. Customers, Projects and Agents (estimate against actual; see CostRollupSection)
 *   4. Daily Spend Trend (line chart)
 *   5. Budget Configuration (form)
 *   6. Billing Verification (Anthropic Admin API reconciliation)
 *   7. Session Detail Log (expandable table)
 *
 * Maya (UI/UX) - Built for clarity at a glance. The CEO should be able
 * to open this page and immediately know: are we on budget, who is
 * spending the most, and what's the trend?
 */

import { CostRollupSection } from './CostRollupSection';
import { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { Card } from '../shared/Card';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';
import { getDispatcherState } from '../../services/dispatcherService';
import type { DispatcherStatus } from '../../types/dispatcher';
import type {
  TokenSummary,
  AgentTokenUsage,
  ProjectTokenUsage,
  TrendDataPoint,
  SessionRecord,
  BudgetConfig,
} from '../../services/tokenService';
import {
  getTokenSummary,
  getTokensByAgent,
  getTokensByProject,
  getTokenTrend,
  getTokenSessions,
  getBudgetConfig,
  updateBudgetConfig,
  getVerificationStatus,
} from '../../services/tokenService';
import { VerificationPanel } from './VerificationPanel';
import { SpendCapPanel } from './SpendCapPanel';

// Register Chart.js components for react-chartjs-2 v5
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler
);

import { fmtNum, fmtTokens } from '../../utils/formatters';

// ── Helpers ────────────────────────────────────────────────────────

/** Format currency: 12.5 -> "$12.50" */
function fmtCost(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format duration in seconds to human-readable */
function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

/** Get initials from a name: "Marcus Chen" -> "MC" */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** Format ISO timestamp to readable date: "2025-01-15T10:30:00Z" -> "Jan 15, 10:30 AM" */
function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Budget utilization color logic */
function utilizationColor(pct: number): { text: string; bg: string; bar: string } {
  if (pct >= 90) return { text: 'text-red-600', bg: 'bg-red-50', bar: 'bg-red-500' };
  if (pct >= 70) return { text: 'text-amber-600', bg: 'bg-amber-50', bar: 'bg-amber-500' };
  return { text: 'text-green-600', bg: 'bg-green-50', bar: 'bg-green-500' };
}

/** Model badge color mapping */
function modelBadgeVariant(model: string): 'purple' | 'info' | 'success' {
  const m = model.toLowerCase();
  if (m.includes('opus')) return 'purple';
  if (m.includes('sonnet')) return 'info';
  return 'success'; // haiku and others
}

// Chart color palette for projects
const PROJECT_COLORS = [
  '#2563EB', // ldaja-blue
  '#7DD3FC', // ldaja-cyan
  '#8B5CF6', // purple
  '#F59E0B', // amber
  '#10B981', // emerald
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
];

// ── Trend period tabs ──────────────────────────────────────────────

type TrendPeriod = 30 | 7 | 1;
const TREND_TABS: { label: string; days: TrendPeriod }[] = [
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Today', days: 1 },
];

// ── Component ──────────────────────────────────────────────────────

export function TokenManagementPage() {
  // ── Data state ─────────────────────────────────────────────────
  const [summary, setSummary] = useState<TokenSummary | null>(null);
  const [agents, setAgents] = useState<AgentTokenUsage[]>([]);
  const [projects, setProjects] = useState<ProjectTokenUsage[]>([]);
  const [trend, setTrend] = useState<TrendDataPoint[]>([]);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [budgetConfig, setBudgetConfig] = useState<BudgetConfig | null>(null);
  const [verifyConfigured, setVerifyConfigured] = useState(false);
  const [dispatcher, setDispatcher] = useState<DispatcherStatus | null>(null);

  // ── UI state ───────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trendDays, setTrendDays] = useState<TrendPeriod>(30);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  // Session filters
  const [sessionAgentFilter, setSessionAgentFilter] = useState('');
  const [sessionProjectFilter, setSessionProjectFilter] = useState('');

  // Budget form state
  const [budgetForm, setBudgetForm] = useState<BudgetConfig | null>(null);
  const [isSavingBudget, setIsSavingBudget] = useState(false);
  const [budgetSaveMsg, setBudgetSaveMsg] = useState('');

  // ── Data fetching ──────────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summaryData, agentData, projectData, trendData, sessionData, configData, verifyStatus] =
        await Promise.all([
          getTokenSummary(),
          getTokensByAgent(),
          getTokensByProject(),
          getTokenTrend(trendDays),
          getTokenSessions({ limit: 50 }),
          getBudgetConfig(),
          getVerificationStatus().catch(() => ({ configured: false })),
        ]);

      setSummary(summaryData);
      setAgents(agentData);
      setProjects(projectData);
      setTrend(trendData);
      setSessions(sessionData);
      setBudgetConfig(configData);
      setBudgetForm(configData);
      setVerifyConfigured(verifyStatus.configured);
    } catch (err) {
      console.error('[TokenManagement] Failed to load data:', err);
      setError('Failed to load token data. Please check the backend connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [trendDays]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Who runs the dispatcher decides what the "no sessions" notice may tell the
  // reader to do. Not knowing is fine: the notice then gives no instruction.
  useEffect(() => {
    let live = true;
    getDispatcherState()
      .then((s) => { if (live) setDispatcher(s); })
      .catch(() => { if (live) setDispatcher(null); });
    return () => { live = false; };
  }, []);

  /** Reload just the trend data when period changes */
  const reloadTrend = useCallback(async (days: TrendPeriod) => {
    try {
      const data = await getTokenTrend(days);
      setTrend(data);
    } catch (err) {
      console.error('[TokenManagement] Failed to reload trend:', err);
    }
  }, []);

  /** Reload sessions when filters change */
  const reloadSessions = useCallback(async () => {
    try {
      const params: { agent?: string; project?: string; limit: number } = { limit: 50 };
      if (sessionAgentFilter) params.agent = sessionAgentFilter;
      if (sessionProjectFilter) params.project = sessionProjectFilter;
      const data = await getTokenSessions(params);
      setSessions(data);
    } catch (err) {
      console.error('[TokenManagement] Failed to reload sessions:', err);
    }
  }, [sessionAgentFilter, sessionProjectFilter]);

  useEffect(() => {
    if (!isLoading) reloadSessions();
  }, [sessionAgentFilter, sessionProjectFilter, reloadSessions, isLoading]);

  /** Handle trend tab change */
  function handleTrendChange(days: TrendPeriod) {
    setTrendDays(days);
    reloadTrend(days);
  }

  /** Save budget config */
  async function handleSaveBudget() {
    if (!budgetForm) return;
    setIsSavingBudget(true);
    setBudgetSaveMsg('');
    try {
      const updated = await updateBudgetConfig(budgetForm);
      setBudgetConfig(updated);
      setBudgetForm(updated);
      setBudgetSaveMsg('Budget configuration saved successfully.');
      setTimeout(() => setBudgetSaveMsg(''), 3000);
    } catch (err) {
      console.error('[TokenManagement] Failed to save budget:', err);
      setBudgetSaveMsg('Failed to save. Please try again.');
    } finally {
      setIsSavingBudget(false);
    }
  }

  /** Reset budget form to last saved values */
  function handleResetBudget() {
    if (budgetConfig) setBudgetForm(budgetConfig);
    setBudgetSaveMsg('');
  }

  // ── Loading & Error states ─────────────────────────────────────

  if (isLoading) {
    return <LoadingSpinner message="Loading token analytics..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Token Data</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadAllData}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Sort agents by total cost descending for the table
  const sortedAgents = [...agents].sort((a, b) => b.totalCost - a.totalCost);

  // Unique agent/project names for session filter dropdowns
  const uniqueAgentNames = [...new Set(sessions.map((s) => s.agentName))].sort();
  const uniqueProjectNames = [...new Set(sessions.map((s) => s.project))].sort();

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Token Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            AI token usage, cost analytics, and budget control
          </p>
        </div>
        <button
          onClick={loadAllData}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* ─── Section 1: Executive Summary Cards ─────────────────── */}
      {summary && <SummaryCards summary={summary} dispatcher={dispatcher} />}

      {/* ─── Section 1b: Agent spend cap and its enforcement switch ─── */}
      <Card title="Agent spend cap" subtitle="Today's and this month's agent spend against the cap">
        <SpendCapPanel />
      </Card>

      {/* ─── Section 2: Cost Breakdown by Agent ─────────────────── */}
      <Card title="Cost Breakdown by Agent" subtitle="Sorted by total cost, highest first">
        <AgentTable agents={sortedAgents} />
      </Card>

      {/* ─── Section 3: Cost by Project ─────────────────────────── */}
      <Card title="Cost by Project" subtitle="Token spend distribution across projects">
        <ProjectBreakdown projects={projects} />
      </Card>

      {/* ─── Section 3b: Estimate against actual, by customer, project and agent ─── */}
      <Card title="Customers, Projects and Agents" subtitle="What was spent beside what was estimated, for the unit and for each customer">
        <CostRollupSection />
      </Card>

      {/* ─── Section 4: Daily Spend Trend ───────────────────────── */}
      <Card
        title="Daily Spend Trend"
        action={
          <div className="flex gap-1">
            {TREND_TABS.map((tab) => (
              <button
                key={tab.days}
                onClick={() => handleTrendChange(tab.days)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  trendDays === tab.days
                    ? 'bg-ldaja-blue text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      >
        <SpendTrendChart trend={trend} />
      </Card>

      {/* ─── Section 5: Budget Configuration ────────────────────── */}
      {budgetForm && (
        <Card title="Budget Configuration" subtitle="Set monthly limits, thresholds, and model pricing">
          <BudgetForm
            form={budgetForm}
            onChange={setBudgetForm}
            onSave={handleSaveBudget}
            onReset={handleResetBudget}
            isSaving={isSavingBudget}
            saveMessage={budgetSaveMsg}
          />
        </Card>
      )}

      {/* ─── Section 6: Billing Verification ────────────────────── */}
      <Card title="Billing Verification" subtitle="Compare local token tracking against Anthropic's Admin API billing data">
        <VerificationPanel isConfigured={verifyConfigured} />
      </Card>

      {/* ─── Section 7: Session Detail Log ──────────────────────── */}
      <Card
        title="Session Detail Log"
        subtitle="Click a row to view the full task description"
        action={
          <div className="flex gap-2">
            <select
              value={sessionAgentFilter}
              onChange={(e) => setSessionAgentFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white"
              aria-label="Filter by agent"
            >
              <option value="">All Agents</option>
              {uniqueAgentNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={sessionProjectFilter}
              onChange={(e) => setSessionProjectFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white"
              aria-label="Filter by project"
            >
              <option value="">All Projects</option>
              {uniqueProjectNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <SessionLog
          sessions={sessions}
          expandedId={expandedSession}
          onToggle={(id) => setExpandedSession(expandedSession === id ? null : id)}
        />
      </Card>
    </div>
  );
}

// ── Section 1: Summary Cards ───────────────────────────────────────

/**
 * What someone who expected sessions should do about there being none. It
 * depends on who runs the dispatcher: only a dispatcher run from this server can
 * be started from the dashboard, and only when it is actually stopped.
 */
function dispatcherAdvice(dispatcher: DispatcherStatus | null): string {
  if (!dispatcher) return '';
  if (dispatcher.managed === 'remote') {
    return ' The dispatcher for this portal runs on another machine and is not controlled from here; if you expected sessions, check that it is running there.';
  }
  if (dispatcher.managed === 'disabled') {
    return ' Dispatcher control is not enabled on this server, so it cannot be started from the portal; if you expected sessions, check the dispatcher where it is run.';
  }
  if (dispatcher.running) {
    return ' The dispatcher is running now, so new sessions will be recorded as agents answer.';
  }
  return ' The dispatcher is stopped. If you expected sessions here, an Admin or Operator can start it from the Agent Dashboard; each agent reply is then a billed Claude call.';
}

function SummaryCards({ summary, dispatcher }: { summary: TokenSummary; dispatcher: DispatcherStatus | null }) {
  const util = summary.budgetUtilization;
  const uColors = utilizationColor(util);
  // Nothing in the window, but something on record: say so, rather than showing
  // five zeroes above a breakdown that is reporting years of usage.
  const quietWindow = summary.recordCount === 0 && summary.lifetimeRecordCount > 0;
  const lastRecorded = summary.lastRecordedAt
    ? new Date(summary.lastRecordedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <>
    {quietWindow && (
      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-semibold">No agent sessions recorded in the last {summary.windowDays} days.</span>{' '}
        The figures below are for that window, so they read zero. Across all time this workspace has recorded{' '}
        {fmtCost(summary.lifetimeSpend)} over {fmtNum(summary.lifetimeRecordCount)} sessions
        {lastRecorded ? `, the most recent on ${lastRecorded}` : ''}. Usage is only recorded while the Agent
        Dispatcher is running.{dispatcherAdvice(dispatcher)}
      </div>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Total Spend (MTD) */}
      <MetricCard
        label="Total Spend (MTD)"
        value={fmtCost(summary.totalSpend)}
        subtitle={`${fmtNum(summary.recordCount)} sessions`}
        color={util >= 90 ? 'red' : util >= 70 ? 'amber' : 'green'}
      />

      {/* Total Tokens Used */}
      <MetricCard
        label="Total Tokens"
        value={fmtTokens(summary.totalInputTokens + summary.totalOutputTokens)}
        subtitle={`In: ${fmtTokens(summary.totalInputTokens)} / Out: ${fmtTokens(summary.totalOutputTokens)}`}
        color="blue"
      />

      {/* Avg Cost Per Session */}
      <MetricCard
        label="Avg Cost / Session"
        value={fmtCost(summary.avgCostPerSession)}
        subtitle="Per agent session"
        color="blue"
      />

      {/* Projected Monthly Spend */}
      <MetricCard
        label="Projected Monthly"
        value={fmtCost(summary.projectedMonthlySpend)}
        subtitle={`Budget: ${fmtCost(summary.monthlyBudget)}`}
        color={summary.projectedMonthlySpend > summary.monthlyBudget ? 'red' : 'green'}
      />

      {/* Budget Utilization */}
      <div className={`${uColors.bg} border border-gray-200 rounded-lg p-4`}>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Budget Used</p>
        <p className={`text-2xl font-bold mt-1 ${uColors.text}`}>{util.toFixed(1)}%</p>
        {/* Progress bar gauge */}
        <div className="mt-3 w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${uColors.bar}`}
            style={{ width: `${Math.min(util, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1.5">
          {fmtCost(summary.totalSpend)} of {fmtCost(summary.monthlyBudget)}
        </p>
      </div>
    </div>
    </>
  );
}

/** Reusable metric card for the summary row */
function MetricCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: string;
  value: string;
  subtitle: string;
  color: 'green' | 'red' | 'amber' | 'blue';
}) {
  const colorMap = {
    green: 'text-green-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-ldaja-blue',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${colorMap[color]}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
    </div>
  );
}

// ── Section 2: Agent Table ─────────────────────────────────────────

function AgentTable({ agents }: { agents: AgentTokenUsage[] }) {
  if (agents.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">No agent usage data available.</p>;
  }

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="px-4 py-2 font-medium">Agent</th>
            <th className="px-4 py-2 font-medium">Model</th>
            <th className="px-4 py-2 font-medium text-right">Tasks</th>
            <th className="px-4 py-2 font-medium text-right">Input Tokens</th>
            <th className="px-4 py-2 font-medium text-right">Output Tokens</th>
            <th className="px-4 py-2 font-medium text-right">Total Cost</th>
            <th className="px-4 py-2 font-medium">Last Active</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {agents.map((agent) => (
            <tr key={agent.agentId} className="hover:bg-gray-50/50 transition-colors">
              {/* Agent avatar + name + role */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {/* Status dot overlaid on avatar */}
                  <div className="relative">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                      style={{ backgroundColor: agent.avatarColor || '#6B7280' }}
                    >
                      {getInitials(agent.agentName)}
                    </div>
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                        agent.status === 'working' || agent.status === 'active'
                          ? 'bg-green-500'
                          : 'bg-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{agent.agentName}</p>
                    <p className="text-xs text-gray-400">{agent.role}</p>
                  </div>
                </div>
              </td>

              {/* Model badge */}
              <td className="px-4 py-3">
                <Badge variant={modelBadgeVariant(agent.model)}>{agent.model}</Badge>
              </td>

              {/* Tasks */}
              <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtNum(agent.tasks)}</td>

              {/* Input Tokens */}
              <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtNum(agent.inputTokens)}</td>

              {/* Output Tokens */}
              <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtNum(agent.outputTokens)}</td>

              {/* Total Cost */}
              <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">
                {fmtCost(agent.totalCost)}
              </td>

              {/* Last Active */}
              <td className="px-4 py-3 text-gray-500 text-xs">{fmtTimestamp(agent.lastActive)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Section 3: Project Breakdown ───────────────────────────────────

function ProjectBreakdown({ projects }: { projects: ProjectTokenUsage[] }) {
  if (projects.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">No project data available.</p>;
  }

  const totalSpend = projects.reduce((sum, p) => sum + p.spend, 0);

  const doughnutData = {
    labels: projects.map((p) => p.project),
    datasets: [
      {
        data: projects.map((p) => p.spend),
        backgroundColor: projects.map((_, i) => PROJECT_COLORS[i % PROJECT_COLORS.length]),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { label?: string; parsed: number }) => {
            const pct = totalSpend > 0 ? ((ctx.parsed / totalSpend) * 100).toFixed(1) : '0';
            return `${ctx.label}: ${fmtCost(ctx.parsed)} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Doughnut Chart */}
      <div className="flex items-center justify-center">
        <div className="relative w-64 h-64">
          <Doughnut data={doughnutData} options={doughnutOptions} />
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs text-gray-400">Total</span>
            <span className="text-lg font-bold text-gray-900">{fmtCost(totalSpend)}</span>
          </div>
        </div>
      </div>

      {/* Project Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {projects.map((project, i) => (
          <div
            key={project.project}
            className="border border-gray-100 rounded-lg p-3 hover:border-gray-200 transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: PROJECT_COLORS[i % PROJECT_COLORS.length] }}
              />
              <span className="text-sm font-medium text-gray-900 truncate">{project.project}</span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Spend</span>
                <span className="font-medium text-gray-700">{fmtCost(project.spend)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Tokens</span>
                <span className="font-medium text-gray-700">{fmtTokens(project.tokens)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Sessions</span>
                <span className="font-medium text-gray-700">{fmtNum(project.sessions)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section 4: Spend Trend Chart ───────────────────────────────────

function SpendTrendChart({ trend }: { trend: TrendDataPoint[] }) {
  if (trend.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">No trend data available.</p>;
  }

  const lineData = {
    labels: trend.map((d) => {
      // Format date labels: "Jan 15" or just time if today
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Daily Spend',
        data: trend.map((d) => d.spend),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: '#2563EB',
      },
      {
        label: 'Budget Threshold',
        data: trend.map((d) => d.budgetThreshold),
        borderColor: '#F59E0B',
        borderDash: [6, 4],
        backgroundColor: 'transparent',
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#9CA3AF' },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#F3F4F6' },
        ticks: {
          font: { size: 11 },
          color: '#9CA3AF',
          callback: (value: string | number) => `$${value}`,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          font: { size: 11 },
          padding: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) =>
            `${ctx.dataset.label}: ${fmtCost(ctx.parsed.y ?? 0)}`,
        },
      },
    },
  };

  return (
    <div className="h-72">
      <Line data={lineData} options={lineOptions} />
    </div>
  );
}

// ── Section 5: Budget Configuration Form ───────────────────────────

function BudgetForm({
  form,
  onChange,
  onSave,
  onReset,
  isSaving,
  saveMessage,
}: {
  form: BudgetConfig;
  onChange: (cfg: BudgetConfig) => void;
  onSave: () => void;
  onReset: () => void;
  isSaving: boolean;
  saveMessage: string;
}) {
  /** Helper to update a top-level field */
  function setField<K extends keyof BudgetConfig>(key: K, value: BudgetConfig[K]) {
    onChange({ ...form, [key]: value });
  }

  /** Helper to update a pricing field */
  function setPricing(model: 'opus' | 'sonnet' | 'haiku', direction: 'input' | 'output', value: number) {
    onChange({
      ...form,
      pricing: {
        ...form.pricing,
        [model]: {
          ...form.pricing[model],
          [direction]: value,
        },
      },
    });
  }

  return (
    <div className="space-y-6">
      {/* Budget Limits */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Monthly Budget</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Monthly Limit ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.monthlyLimit}
              onChange={(e) => setField('monthlyLimit', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Warning Threshold (%)</label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={form.warningThreshold}
              onChange={(e) => setField('warningThreshold', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Critical Threshold (%)</label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={form.criticalThreshold}
              onChange={(e) => setField('criticalThreshold', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
            />
          </div>
        </div>
      </div>

      {/* Model Pricing */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Model Pricing (per 1M tokens)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(['opus', 'sonnet', 'haiku'] as const).map((model) => (
            <div key={model} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant={model === 'opus' ? 'purple' : model === 'sonnet' ? 'info' : 'success'}>
                  {model.charAt(0).toUpperCase() + model.slice(1)}
                </Badge>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Input ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.pricing[model].input}
                    onChange={(e) => setPricing(model, 'input', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Output ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.pricing[model].output}
                    onChange={(e) => setPricing(model, 'output', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          onClick={onReset}
          disabled={isSaving}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Reset
        </button>
        {saveMessage && (
          <span
            className={`text-sm ${
              saveMessage.includes('success') ? 'text-green-600' : 'text-red-500'
            }`}
          >
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Section 6: Session Detail Log ──────────────────────────────────

function SessionLog({
  sessions,
  expandedId,
  onToggle,
}: {
  sessions: SessionRecord[];
  expandedId: string | null;
  onToggle: (id: string) => void;
}) {
  if (sessions.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">No session records found.</p>;
  }

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="px-4 py-2 font-medium w-5"></th>
            <th className="px-4 py-2 font-medium">Timestamp</th>
            <th className="px-4 py-2 font-medium">Agent</th>
            <th className="px-4 py-2 font-medium">Project</th>
            <th className="px-4 py-2 font-medium">Model</th>
            <th className="px-4 py-2 font-medium text-right">Input</th>
            <th className="px-4 py-2 font-medium text-right">Output</th>
            <th className="px-4 py-2 font-medium text-right">Cost</th>
            <th className="px-4 py-2 font-medium text-right">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sessions.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              isExpanded={expandedId === session.id}
              onToggle={() => onToggle(session.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionRow({
  session,
  isExpanded,
  onToggle,
}: {
  session: SessionRecord;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isExpanded}
      >
        {/* Expand chevron */}
        <td className="px-4 py-3 text-gray-400">
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </td>
        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtTimestamp(session.timestamp)}</td>
        <td className="px-4 py-3 font-medium text-gray-900">{session.agentName}</td>
        <td className="px-4 py-3 text-gray-600">{session.project}</td>
        <td className="px-4 py-3">
          <Badge variant={modelBadgeVariant(session.model)}>{session.model}</Badge>
        </td>
        <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtNum(session.inputTokens)}</td>
        <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtNum(session.outputTokens)}</td>
        <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">
          {fmtCost(session.cost)}
        </td>
        <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{fmtDuration(session.duration)}</td>
      </tr>

      {/* Expanded detail row showing task description */}
      {isExpanded && (
        <tr>
          <td colSpan={9} className="px-4 py-3 bg-gray-50/70">
            <div className="pl-9">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Task Description
              </span>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                {session.taskDescription || 'No description recorded.'}
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
